import React, { useState } from 'react';
import { Modal, TextInput, Select, Button, Box, Stack, Text } from '@mantine/core';
import { subscriptionApi } from '../apis/subscription.api';
import { toast } from 'react-toastify';
import { getRegionLabel } from '../utils/regionHelper';

const AddUsers = ({ 
  onClose, 
  organizationId, 
  onSubmit, 
  loading: parentLoading, 
  maxUsers,
  currentUserCount 
}) => {
  const [users, setUsers] = useState([{ 
    name: '',
    email: '', 
    role: 'trainee',
    region: process.env.REACT_APP_REGION_CODE?.toLowerCase() || 'us-east-1'
  }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const remainingSlots = Math.max(0, maxUsers - currentUserCount);

  const handleAddAnotherUser = () => {
    const wouldExceedLimit = users.length >= remainingSlots;
    if (wouldExceedLimit) {
      toast.error(
        `Cannot add more users. You can add up to ${remainingSlots} more user${remainingSlots === 1 ? '' : 's'} with your current subscription.`, 
        { autoClose: 6000 }
      );
      return;
    }
    setUsers([...users, { name: '', email: '', role: 'trainee', region: 'us-east-1' }]);
  };

  const handleRemoveUser = (index) => {
    const newUsers = users.filter((_, i) => i !== index);
    setUsers(newUsers);
  };

  const handleUserChange = (index, field, value) => {
    const newUsers = [...users];
    newUsers[index] = { ...newUsers[index], [field]: value };
    setUsers(newUsers);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validate all users before submitting
      const invalidUsers = users.filter(user => !user.email.trim() || !user.name.trim());
      if (invalidUsers.length > 0) {
        throw new Error('All users must have a name and an email address');
      }

      if (users.length > remainingSlots) {
        throw new Error(
          `Cannot add ${users.length} users. You can only add up to ${remainingSlots} more user${remainingSlots === 1 ? '' : 's'} with your current subscription.`
        );
      }

      await onSubmit(users);
      
    } catch (err) {
      console.error('Error in AddUsers submit:', err);
      setError(err.message || 'Failed to add users to queue. Please try again.');
      toast.error(err.message || 'Failed to add users to queue. Please try again.', {
        autoClose: 6000 
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update the Select component's data prop
  const regionData = (process.env.REACT_APP_REGIONS?.split(',') || [])
    .map((code, index) => ({
      value: code.toLowerCase(),
      label: (process.env.REACT_APP_REGION_LABELS?.split(',') || [])[index] || code
    }));

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title="Add Users"
      size="xl"
      padding="md"
      styles={{
        header: {
          backgroundColor: '#f8f9fa',
          padding: '16px 24px',
          marginBottom: '16px',
        },
        title: {
          fontWeight: 600,
          fontSize: '1.1rem',
        }
      }}
    >
      <Stack spacing="lg">
        {error && (
          <Text color="red" size="sm" weight={500} sx={{ textAlign: 'center' }}>
            {error}
          </Text>
        )}
        
        {users.map((user, index) => (
          <Box 
            key={index}
            sx={{
              display: 'grid',
              gridTemplateColumns: '28% 32% 16% 16% 5%',
              gap: '16px',
              alignItems: 'flex-start',
            }}
          >
            <TextInput
              required
              label="Name"
              value={user.name}
              onChange={(e) => handleUserChange(index, 'name', e.target.value)}
              error={user.name.trim() === '' ? 'Name is required' : null}
              styles={{
                input: {
                  height: '36px',
                  '&:focus': {
                    borderColor: '#228be6',
                  },
                },
                label: {
                  marginBottom: '4px',
                  fontWeight: 500,
                }
              }}
            />
            <TextInput
              required
              label="Email"
              value={user.email}
              onChange={(e) => handleUserChange(index, 'email', e.target.value)}
              error={user.email.trim() === '' ? 'Email is required' : null}
              styles={{
                input: {
                  height: '36px',
                  '&:focus': {
                    borderColor: '#228be6',
                  },
                },
                label: {
                  marginBottom: '4px',
                  fontWeight: 500,
                }
              }}
            />
            <Select
              label="Role"
              value={user.role}
              onChange={(value) => handleUserChange(index, 'role', value)}
              data={[
                { value: 'leader', label: 'Leader' },
                { value: 'trainee', label: 'Trainee' }
              ]}
              styles={{
                input: { height: '36px' },
                label: {
                  marginBottom: '4px',
                  fontWeight: 500,
                }
              }}
            />
            <Select
              label="Region"
              value={user.region}
              onChange={(value) => handleUserChange(index, 'region', value)}
              data={regionData}
              disabled={true}
              styles={{
                input: { 
                  height: '36px',
                  backgroundColor: '#f8f9fa'
                },
                label: {
                  marginBottom: '4px',
                  fontWeight: 500,
                }
              }}
            />
            {users.length > 1 && (
              <Button 
                color="red"
                variant="subtle"
                onClick={() => handleRemoveUser(index)}
                sx={{
                  marginTop: '24px',
                  marginLeft: '-12px',
                  height: '36px',
                  padding: '0 2px',
                }}
              >
                x
              </Button>
            )}
          </Box>
        ))}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '16px',
            padding: '16px 0',
            borderTop: '1px solid #e9ecef',
          }}
        >
          <Button 
            variant="outline" 
            onClick={handleAddAnotherUser}
            sx={{ height: '36px' }}
          >
            Add Another User
          </Button>
          <Box sx={{ display: 'flex', gap: '12px' }}>
            <Button 
              variant="subtle" 
              onClick={onClose}
              sx={{ height: '36px' }}
            >
              Cancel
            </Button>
            <Button 
              loading={loading}
              onClick={handleSubmit}
              disabled={users.some(user => !user.email.trim() || !user.name.trim())}
              sx={{ height: '36px' }}
            >
              Add Users
            </Button>
          </Box>
        </Box>

        <Text size="sm" color="dimmed" mt={-8}>
        After adding user, they must register at their assigned regional server:<br />
          US = app.lavenirai.com&nbsp;&nbsp;&nbsp;
          EU = app-eu.lavenirai.com&nbsp;&nbsp;&nbsp;
          APAC = app-sg.lavenirai.com
        </Text>
      </Stack>
    </Modal>
  );
};

export default AddUsers;